/**
 * Reading posters — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_everyday_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const readingPostersPack: ToilePack = {
    slug: 'reading-posters',
    category: 'classroom',
    name: 'Reading posters',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'tree', label: 'Under a tree', subject: 'a child reading against a big leafy tree with birds overhead and the word "READ" in large letters'},
        {key: 'adventure', label: 'Adventure', subject: 'an open book with a sailing ship, mountains and a hot-air balloon rising out of its pages and the words "Books Take You Places"'},
        {key: 'cozy', label: 'Cozy corner', subject: 'a cat asleep on a stack of books beside a mug and a lamp with the words "Just One More Chapter"'},
    ],
    styles: [
        {key: 'storybook', label: 'Storybook', style: "children's storybook illustration, soft watercolor shapes with gentle outlines and a warm friendly mood", palette: 'a soft palette of sky blue, leaf green, warm yellow and pink', framing: 'Full-bleed, the words set large and legible and the scene filling the rest.'},
        {key: 'papercut', label: 'Paper cut', style: 'layered paper-cut collage, flat shapes with soft drop shadows between the layers, hard edges', palette: 'a bright palette of teal, coral, sunflower yellow and cream', framing: 'Full-bleed, the words set large and legible and the scene filling the rest.'},
        {key: 'chalk', label: 'Chalkboard', style: 'chalkboard classroom poster, hand-drawn chalk lettering and doodles with a dusty chalk texture', palette: 'a palette of white, pastel yellow and pastel pink chalk on deep green', framing: 'Full-bleed, the words set large and legible and the scene filling the rest.'},
    ],
    directions: ['a dog', 'owl', 'bedtime'],
    directionHint: 'try: a dog',
    seo: {
        title: 'Reading Posters for Classrooms and Libraries',
        metaDescription: 'Nine reading posters for classrooms and libraries: a child under a tree, an adventure book and a cat on books, in three styles.',
        h1: 'Reading posters: child under a tree, adventure book, cat on books',
        tagline: 'Three poster scenes that invite children to pick up a book, in three classroom styles.',
        intro: 'Teachers and librarians hang posters that make children want to pick up a book. These nine posters show a child reading against a big tree with the word READ; an open book with a ship, mountains and a hot-air balloon rising from its pages; and a cat asleep on a stack of books beside a lamp. Each comes in a storybook watercolour, a paper cut or a chalkboard style. Type a request, such as an owl, to shape it.',
        useCases: [
            'a classroom reading corner',
            'a library wall',
            'a home reading nook',
            'a school bulletin board',
        ],
        faq: [
            {q: 'Do the posters have text?', a: 'Yes. Each carries a short line: READ, Books Take You Places, or Just One More Chapter.'},
            {q: 'What are the three styles?', a: 'Storybook is soft watercolour with gentle outlines, paper cut is layered shapes with soft shadows in teal, coral and sunflower yellow, and chalkboard is pastel chalk on deep green.'},
            {q: 'Can I ask for a different animal or scene?', a: 'Yes, as a request, such as a dog or a bedtime scene. The nine posters show the scenes above.'},
        ],
    },
}
